import { API_BASE, fetchJson, clearSearchCache } from "../utils";

/** @typedef {"unreviewed" | "allowed" | "blocked"} VariationStatus */
/** @typedef {VariationStatus | "all"} VariationFilter */
/** @typedef {"allowed" | "blocked"} ReviewDecision */
/** @typedef {{id: number, name: string, status: VariationStatus}} NameVariationDto */
/** @typedef {{id: number, name: string, name_variations: NameVariationDto[]}} ArtistVariationGroupDto */
/** @typedef {{artists: ArtistVariationGroupDto[], page: number, page_size: number, total: number}} ArtistVariationPageDto */
/** @typedef {{id: number, artist_id: number, name: string, status: ReviewDecision}} ReviewVariationResponseDto */
// Preserve the existing ArtistPage detail shape rather than mapping it to a review response.
/** @typedef {{id: number, name: string, image: *, albums: Object[]}} ArtistDetailDto */

export const REVIEW_FILTERS = ["unreviewed", "allowed", "blocked", "all"];
const isId = (value) => Number.isSafeInteger(value) && value > 0;
const isVariation = (value) =>
  isId(value?.id) &&
  typeof value.name === "string" &&
  REVIEW_FILTERS.slice(0, 3).includes(value.status);

export function validateReviewPage(data) {
  if (
    !Array.isArray(data?.artists) ||
    !isId(data.page) ||
    !isId(data.page_size) ||
    data.page_size > 100 ||
    !Number.isSafeInteger(data.total) ||
    data.total < 0 ||
    !data.artists.every(
      (artist) =>
        isId(artist?.id) &&
        typeof artist.name === "string" &&
        Array.isArray(artist.name_variations) &&
        artist.name_variations.every(isVariation),
    )
  ) {
    throw new Error("Invalid artist review response. Refresh to try again.");
  }
  return data;
}

/** @returns {Promise<ArtistVariationPageDto>} */
export async function listArtistVariations({
  filter = "unreviewed",
  page = 1,
  pageSize = 25,
  search = "",
  signal,
} = {}) {
  const query = new URLSearchParams({
    status: filter,
    page,
    page_size: pageSize,
  });
  if (search.trim()) query.set("q", search);
  return validateReviewPage(
    await fetchJson(`${API_BASE}/admin/artist-name-variations/?${query}`, {
      signal,
    }),
  );
}

/** @returns {Promise<ReviewVariationResponseDto>} */
export async function reviewVariation(variationId, artistId, decision) {
  if (!["allowed", "blocked"].includes(decision))
    throw new Error("Invalid review decision");
  const data = await fetchJson(
    `${API_BASE}/admin/artist-name-variations/${variationId}/`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: decision }),
    },
  );
  if (
    !isVariation(data) ||
    data.id !== variationId ||
    data.artist_id !== artistId ||
    !["allowed", "blocked"].includes(data.status)
  )
    throw new Error(
      "Invalid review decision response. Refresh before retrying.",
    );
  return data;
}

export function validateArtistName(name) {
  if (typeof name !== "string" || !name.trim())
    return "Enter a name with at least one non-whitespace character.";
  if (Array.from(name).length > 255)
    return "Name must be at most 255 characters.";
  return "";
}

/** @returns {Promise<ArtistDetailDto>} */
export async function renameArtist(artistId, name) {
  const error = validateArtistName(name);
  if (error) throw new Error(error);
  const data = await fetchJson(`${API_BASE}/artists/${artistId}/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (
    data?.id !== artistId ||
    typeof data.name !== "string" ||
    !Array.isArray(data.albums)
  ) {
    throw new Error("Invalid artist detail response. Refresh before retrying.");
  }
  // Other views fetch component-local data on mounting; search is the only shared cache.
  clearSearchCache();
  return data;
}

export const artistImageUrl = (id) => `${API_BASE}/artists/${id}/image/`;

export function reconcileDecision(page, result, filter) {
  if (!page) return page;
  const artists = page.artists
    .map((artist) =>
      artist.id !== result.artist_id
        ? artist
        : {
            ...artist,
            name_variations: artist.name_variations
              .map((variation) =>
                variation.id === result.id
                  ? { id: result.id, name: result.name, status: result.status }
                  : variation,
              )
              .filter(
                (variation) => filter === "all" || variation.status === filter,
              ),
          },
    )
    .filter((artist) => artist.name_variations.length > 0);
  return {
    ...page,
    artists,
    total: Math.max(0, page.total - (page.artists.length - artists.length)),
  };
}
